// Диспетчер карт Влияния Деуса (А.4.4.2).
// Карты Влияния НЕ в колоде. Игрок видит их в стенде Влияния, играет в Ф2.
// Могут быть с целью (Кумовство) или без (остальные).
//
// API:
//   canPlay(state, playerIdx, cardId)         → {ok, reason?}
//   needsTarget(cardId)                        → bool
//   play(state, playerIdx, cardId, targetCard?) → {ok, reason?}
//   getStandCardIds(faction)                   → массив cardId-ов в порядке отображения

import { CARD_ID as ID_PARASHYUT, canPlayParachute, playParachute } from './parashyut.js';
import { CARD_ID as ID_SCHIT,     canPlayShield,    playShield, COST as COST_SCHIT } from './aktivnyy-shchit.js';
import { CARD_ID as ID_STIM,      canPlayStimulator, playStimulator, COST as COST_STIM } from './eksperimentalnyy-stimulyator.js';
import { CARD_ID as ID_NABOR,     canPlayNabor,     playNabor, COST as COST_NABOR } from './nabor-v-korporatsiyu.js';
import { CARD_ID as ID_KUMOV,     canPlayKumovstvo, playKumovstvo, COST as COST_KUMOV } from './kumovstvo.js';
import { CARD_ID as ID_SPY,       canPlaySpy,       playSpy, COST as COST_SPY } from './spyaschiy-agent.js';
import { COST as COST_PARASHYUT } from './parashyut.js';
import { getFactionCards } from '../../../data/decks.js';
// C.1 Этап 34: 13 v6 карт Влияния-юнитов (всё кроме Спящего).
import {
  isC1InfluenceCard, canPlayInfluenceC1, playInfluenceC1, needsTargetInfC1,
  getInfluenceTargetsC1, C1_INFLUENCE_IDS
} from './c1/influence-c1.js';

// C.1 Этап 33: fallback-шаблоны для старых карт Влияния, удалённых из v6 xlsx.
// Реализация в коде есть, но cards-deus.js (gen-cards) их не содержит → UI
// не может отрисовать имя/описание/cost. Эти hardcoded-шаблоны используются
// только в renderInfluenceStrip и тултипах, на боевую логику не влияют.
const INFLUENCE_FALLBACK_TEMPLATES = {
  [ID_PARASHYUT]: {
    id: ID_PARASHYUT, name_ru: 'Парашют', faction: 'deus',
    type: 'influence', rarity: 'special', influence_cost: COST_PARASHYUT,
    ability_short: 'Парашют Героя — поглощает 1 смертельный удар',
    ability_text: { active: 'Парашют Героя — при первом смертельном ударе срабатывает и Герой остаётся с 1 ХП. Одноразовый.' }
  },
  [ID_SCHIT]: {
    id: ID_SCHIT, name_ru: 'Активный щит', faction: 'deus',
    type: 'influence', rarity: 'special', influence_cost: COST_SCHIT,
    ability_short: 'Иммунитет Герою на 1 ход',
    ability_text: { active: 'Герою — Иммунитет (поглощает следующий удар), восстанавливается в Ф1 каждого хода пока юнит-носитель жив.' }
  },
  [ID_STIM]: {
    id: ID_STIM, name_ru: 'Экспериментальный стимулятор', faction: 'deus',
    type: 'influence', rarity: 'special', influence_cost: COST_STIM,
    ability_short: '+1/0/+1 Герою до конца матча',
    ability_text: { active: 'Героя баффает: +1 атаки и +1 ХП до конца матча.' }
  },
  [ID_NABOR]: {
    id: ID_NABOR, name_ru: 'Набор в корпорацию', faction: 'deus',
    type: 'influence', rarity: 'special', influence_cost: COST_NABOR,
    ability_short: 'Призывает Стажёра 2/0/2',
    ability_text: { active: 'Призывает на твою сторону Стажёра 2/0/2 в свободную ячейку.' }
  },
  [ID_KUMOV]: {
    id: ID_KUMOV, name_ru: 'Кумовство', faction: 'deus',
    type: 'influence', rarity: 'special', influence_cost: COST_KUMOV,
    ability_short: 'Цель: союзный юнит — +1/0/+1',
    ability_text: { active: 'Выбранный союзный юнит получает +1 атаки и +1 ХП постоянно.' }
  }
};

const TARGETED = new Set([ID_KUMOV]);

// C.1 Этап 34: реализованные карты Влияния Деуса.
// Стенд теперь содержит ВСЕ 14 v6 карт (Спящий + 13 v6-юнитов).
// Старые 5 fallback-карт (Парашют/Щит/Стимулятор/Набор/Кумовство) ОТКЛЮЧЕНЫ —
// они не существуют в v6 xlsx и не должны быть в оффере. Реализация в коде
// сохранена для backward-compat (тесты, старые сценарии).
const IMPLEMENTED_INFLUENCE_IDS = [
  ID_SPY,
  ...C1_INFLUENCE_IDS
];
// Старое имя сохраняем — bot-random.js использует его в цикле.
export const STAND_ORDER = IMPLEMENTED_INFLUENCE_IDS;

// C.1 Этап 33: лимит копий — каждую карту Влияния можно сыграть не более 2 раз за матч.
export const INFLUENCE_PER_CARD_LIMIT = 2;

export function needsTarget(cardId) {
  if (isC1InfluenceCard(cardId)) return needsTargetInfC1(cardId);
  return TARGETED.has(cardId);
}

// C.1 Этап 34: Targets-провайдер для v6 Влияния (Ликвидатор требует вражеский юнит).
export function getInfluenceTargets(state, playerIdx, cardId) {
  if (isC1InfluenceCard(cardId)) return getInfluenceTargetsC1(state, playerIdx, cardId);
  return [];
}

// Получить счётчик розыгрышей данной карты у игрока за матч.
export function getInfluencePlayCount(player, cardId) {
  return player?.influencePlayCounts?.[cardId] ?? 0;
}

// C.1 Этап 33: roll 3 случайных карты Влияния для игрока — кладёт в
// player.influenceOffer. Учитывает лимит 2 копии за матч (исключает «исчерпанные»).
// Вызывается в startTurn для обоих игроков. Если реализованных карт < 3 — оффер
// содержит сколько есть.
export function rollInfluenceOffer(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p) return [];
  // Бот тоже получает оффер — он не должен играть с полного стенда.
  const pool = IMPLEMENTED_INFLUENCE_IDS.filter(
    id => getInfluencePlayCount(p, id) < INFLUENCE_PER_CARD_LIMIT
  );
  const rng = state.rng;
  // Fisher–Yates shuffle.
  const arr = [...pool];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng?.int ? rng.int(i + 1) : Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const offer = arr.slice(0, 3);
  p.influenceOffer = offer;
  return offer;
}

export function canPlayInfluenceCard(state, playerIdx, cardId) {
  // C.1 Этап 33: проверка оффера и лимита 2 копии.
  const p = state.players?.[playerIdx];
  if (p && Array.isArray(p.influenceOffer) && !p.influenceOffer.includes(cardId)) {
    return { ok: false, reason: 'not_in_offer' };
  }
  if (p && getInfluencePlayCount(p, cardId) >= INFLUENCE_PER_CARD_LIMIT) {
    return { ok: false, reason: 'limit_reached' };
  }
  // C.1 Этап 34: сначала проверяем C1-карты (v6 ID).
  if (isC1InfluenceCard(cardId)) {
    return canPlayInfluenceC1(state, playerIdx, cardId);
  }
  let inner;
  switch (cardId) {
    case ID_PARASHYUT: inner = canPlayParachute(state, playerIdx); break;
    case ID_SCHIT:     inner = canPlayShield(state, playerIdx); break;
    case ID_STIM:      inner = canPlayStimulator(state, playerIdx); break;
    case ID_NABOR:     inner = canPlayNabor(state, playerIdx); break;
    case ID_KUMOV:     inner = canPlayKumovstvo(state, playerIdx); break;
    case ID_SPY:       inner = canPlaySpy(state, playerIdx); break;
    default:           inner = { ok: false, reason: 'unknown_card' }; break;
  }
  return inner;
}

// Розыгрыш карты Влияния. Для Кумовства — обязателен targetCard (союзный юнит).
export function playInfluenceCard(state, playerIdx, cardId, targetCard = null) {
  let result;
  // C.1 Этап 34: сначала v6-карты.
  if (isC1InfluenceCard(cardId)) {
    result = playInfluenceC1(state, playerIdx, cardId, targetCard);
  } else {
    switch (cardId) {
      case ID_PARASHYUT: result = playParachute(state, playerIdx); break;
      case ID_SCHIT:     result = playShield(state, playerIdx); break;
      case ID_STIM:      result = playStimulator(state, playerIdx); break;
      case ID_NABOR:     result = playNabor(state, playerIdx); break;
      case ID_KUMOV:     result = playKumovstvo(state, playerIdx, targetCard); break;
      case ID_SPY:       result = playSpy(state, playerIdx); break;
      default:           result = { ok: false, reason: 'unknown_card' }; break;
    }
  }
  // C.1 Этап 33: инкремент счётчика только при успешном розыгрыше.
  if (result?.ok) {
    const p = state.players[playerIdx];
    if (!p.influencePlayCounts) p.influencePlayCounts = {};
    p.influencePlayCounts[cardId] = (p.influencePlayCounts[cardId] || 0) + 1;
    // Удалить карту из оффера (нельзя сыграть её ещё раз в ЭТОМ ходу из оффера).
    if (Array.isArray(p.influenceOffer)) {
      p.influenceOffer = p.influenceOffer.filter(id => id !== cardId);
    }
  }
  return result;
}

// Получить шаблоны карт по ID для UI (имя, cost, ability_text).
// Сначала пытаемся из cards-deus.js (v6 xlsx), затем fallback на hardcoded.
export function getInfluenceCardTemplates(faction, cardIds) {
  const all = getFactionCards(faction || 'deus');
  return cardIds
    .map(id => all.find(c => c.id === id) || INFLUENCE_FALLBACK_TEMPLATES[id])
    .filter(Boolean);
}
